import { AlertTriangle } from 'lucide-react'
import ComboField from '@/components/ComboField'

// Variety input that follows the picked block (spec deliveries-corrections
// §2.6). Picking a block used to auto-fill the variety and leave it free to
// overtype with nothing to say so — a typo there does not resolve to a
// commodity, which un-bills the delivery and blocks run creation.
//
// Locked = the block has a variety, the value still matches it, and the
// operator has not asked for a different one: a read-only field plus one
// button. Once the value differs from the block (an override, or a row saved
// that way), the combo is live and an amber line names the block's variety.
interface Props {
  id:           string
  value:        string
  onChange:     (v: string) => void
  options:      string[]
  /** The picked block's variety; null/'' when no block or the block has none. */
  blockVariety: string | null | undefined
  override:     boolean
  onOverride:   (v: boolean) => void
  required?:    boolean
}

function same(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase()
}

function LockedVariety({ id, value, required, onUnlock }: {
  id: string; value: string; required?: boolean; onUnlock: () => void
}) {
  return (
    <div>
      <label className="label" htmlFor={id}>
        Variety{required && <span className="text-red-400"> *</span>}
      </label>
      <div className="flex items-center gap-2">
        <input id={id} className="field flex-1" value={value} readOnly
          aria-describedby={`${id}-from-block`} />
        <button type="button" className="btn-ghost btn-sm whitespace-nowrap" onClick={onUnlock}>
          Different variety on this truck
        </button>
      </div>
      <p id={`${id}-from-block`} className="text-[11px] text-muted mt-1">
        From the block. Tap the button if the truck carries something else.
      </p>
    </div>
  )
}

export default function VarietyFromBlockField({
  id, value, onChange, options, blockVariety, override, onOverride, required,
}: Props) {
  const bv = blockVariety?.trim() || ''
  if (bv && !override && same(value, bv)) {
    return <LockedVariety id={id} value={value} required={required} onUnlock={() => onOverride(true)} />
  }
  const differs = !!bv && !same(value, bv)
  return (
    <div>
      <ComboField id={id} label="Variety" required={required}
        value={value} onChange={onChange} options={options}
        placeholder={bv ? 'Type the variety on the truck' : 'Auto-filled or type'} />
      {differs && (
        <p className="text-[11px] text-amber-300 mt-1 flex items-center gap-1" role="status">
          <AlertTriangle size={11} /> Differs from the block&apos;s variety ({bv}).
        </p>
      )}
      {!!bv && (
        <button type="button" className="text-[11px] text-fg-muted underline mt-1"
          onClick={() => { onOverride(false); onChange(bv) }}>
          Use the block&apos;s variety ({bv})
        </button>
      )}
    </div>
  )
}
